import type { ReactNode } from "react";

/**
 * A Stats panel.
 *
 * **Every value here is the design's own computed style, read off the rendered frames** — not a
 * judgement from `tokens.css`. The first pass was built the other way round and came out visibly
 * wrong; three things it got wrong are worth naming, because each looks like a preference and is
 * actually a measurement:
 *
 * - **The heading sits INSIDE the card.** `8a`, `8b` and `8d` all put it there; only Coverage's two
 *   tables put it outside, which is what `variant="flush"` is for. Building the outside form for
 *   every panel — from the one frame that uses it — is most of what made the surface read as a
 *   different design.
 * - **The card is `1px #cfcbc1`, radius 10, `15px 18px`, no shadow.** `--line-strong`, not `--line`.
 * - **`rounded-card` was not a class.** It, `rounded-button`, `rounded-chip` and `rounded-inner`
 *   were invented here and Tailwind generated none of them, so every card in this surface was
 *   square. They are gone: radii come from `--r-2` / `--r-3` / `--r-4` since 日本-391, which is a
 *   scale rather than a set of names to get wrong.
 *
 * A big figure is **sans-serif 600**, and only tables and key/value rows are mono — see `StatValue`.
 */
export function Panel({
  title,
  note,
  right,
  children,
  gap = 9,
  variant = "card",
  check,
}: {
  title: string;
  /**
   * The `checks/` hook, as `data-check="panel.<id>"`.
   *
   * Required rather than optional, and not derived from `title`: the titles are about to be
   * Japanese, and a panel with no stable handle is one the browser checks can only find by its
   * words. It also removes a trap `check-stats.mjs` documents at length — locating a panel by
   * `hasText` is a substring match over the whole subtree, so `Difficulty` matched *Predicted
   * recall*, whose FSRS-off prose happens to begin "Difficulty, stability and predicted recall come
   * from FSRS", and the check then asserted geometry about the wrong card.
   */
  check: string;
  note?: string;
  /** Right-aligned on the heading row — a legend, a control. */
  right?: ReactNode;
  children: ReactNode;
  /** Vertical rhythm between the panel's own blocks. The frames use 8–10px inside a card. */
  gap?: number;
  /**
   * `card` — the heading inside a padded card. Everything but Coverage.
   * `flush` — heading outside, card with `6px 0` padding so a table's row rules reach its border.
   * `bare` — heading outside, no card, for a section whose content is its own row of cards.
   */
  variant?: "card" | "flush" | "bare";
}) {
  // `gap-y` is what a WRAPPED heading row gets, and 2px was written for a wrapped `note` — two
  // lines of the same 11px text, where more would read as a gap. It is wrong the moment `right`
  // holds a control: at 352px the target button drops onto its own line and sat 2px under the
  // title, touching it. 7px is the row rhythm the rest of the panel already uses, and it is
  // invisible whenever the heading fits on one line.
  const heading = (
    <div className="flex flex-wrap items-baseline gap-x-[9px] gap-y-[7px]">
      <h2 className="text-[14px]/[14px] font-semibold uppercase tracking-[1.1px] text-ink-3">
        {title}
      </h2>
      {note && <span className="text-[14px]/[14px] text-ink-4">{note}</span>}
      {right && (
        <>
          <div className="flex-1" />
          {right}
        </>
      )}
    </div>
  );

  if (variant === "card") {
    return (
      <section
        data-check={`panel.${check}`}
        className="flex flex-col rounded-[var(--r-3)] border border-line-strong bg-panel px-[18px] py-[15px]"
        style={{ gap }}
      >
        {heading}
        {children}
      </section>
    );
  }

  return (
    <section data-check={`panel.${check}`} className="flex flex-col">
      <div className="pb-[8px]">{heading}</div>
      {variant === "flush" ? (
        <div className="rounded-[var(--r-3)] border border-line-strong bg-panel py-[6px]">
          {children}
        </div>
      ) : (
        children
      )}
    </section>
  );
}

/**
 * The tinted box the frames use for a footnote — Coverage's un-enriched count, Pace's definition.
 *
 * `#e3e0d9` on `--sunken`, deliberately *not* the white card: it is commentary on the panels above
 * rather than a panel of its own, and a bare grey paragraph (what this was) loses that entirely.
 */
export function FootnotePanel({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-[var(--r-3)] border border-line bg-sunken px-[16px] py-[11px] text-[14px]/[1.5] text-ink-2">
      {children}
    </div>
  );
}

/**
 * A column header. `600 10px`, uppercase, 0.5px tracking, `--ink-4` — the design's, measured.
 *
 * Small caps rather than sentence case because the header is furniture: at 10px uppercase it reads
 * as a label for the column beneath rather than competing with the numbers in it.
 */
export function Th({
  children,
  align = "right",
  width,
}: {
  children?: ReactNode;
  align?: "left" | "right";
  width?: number;
}) {
  return (
    <th
      className={`px-[6px] pb-[6px] text-[14px]/[19.6px] font-semibold uppercase tracking-[.5px] text-ink-4 ${
        align === "left" ? "text-left" : "text-right"
      }`}
      style={width ? { width } : undefined}
    >
      {children}
    </th>
  );
}

/**
 * A table cell. `600 12px` mono — the design's table values are all `ui-monospace`, which is what
 * makes a column of figures line up on the decimal point.
 *
 * `tone` carries the hierarchy the design has and a flat table loses: the numbers a reader is
 * scanning for are `--ink`, the supporting ones `--ink-3`.
 */
export function Td({
  children,
  align = "right",
  tone = "ink",
  mono = true,
  color,
  title,
}: {
  children: ReactNode;
  align?: "left" | "right";
  tone?: "ink" | "muted";
  mono?: boolean;
  color?: string;
  /** Native tooltip. The right answer for a table cell: there is no mark to outline, and a figure
   *  that wants one line of context does not need the hover card's machinery. */
  title?: string;
}) {
  return (
    <td
      title={title}
      className={`px-[6px] py-[7px] text-[14px]/[19.6px] font-semibold ${
        mono ? "font-mono" : ""
      } ${align === "left" ? "text-left" : "text-right"} ${
        color ? "" : tone === "muted" ? "text-ink-3" : "text-ink"
      }`}
      style={color ? { color } : undefined}
    >
      {children}
    </td>
  );
}

/**
 * The label column of a table row: `600 12px`, ink, never mono.
 *
 * No left padding by default — inside a `card` panel the card's own 18px is the inset, and adding
 * another 16px would push the labels a third of the way across a narrow panel. `inset` is for the
 * `flush` variant, whose card has no horizontal padding of its own.
 */
export function RowLabel({ children, inset = false }: { children: ReactNode; inset?: boolean }) {
  return (
    <td
      className={`py-[7px] pr-[10px] text-[14px]/[19.6px] font-semibold text-ink ${
        inset ? "pl-[16px]" : ""
      }`}
    >
      {children}
    </td>
  );
}

/**
 * A key/value list. Rows are the same 12px as a table's, values mono.
 *
 * **Every row draws its top rule, including the first**, which is the design's treatment in all
 * three places it uses this shape (Lapses, pass-rate-by-difficulty, the stat strips) — so the
 * heading is separated from the rows beneath it by the same hairline that separates the rows from
 * each other. It used to suppress the first (`first:border-t-0`), which reads as a missing divider
 * on every panel built from a list rather than a table (Alex, 8 Aug 2026).
 */
export function StatList({ rows }: { rows: [string, string][] }) {
  return (
    <dl className="flex flex-col">
      {rows.map(([key, value]) => (
        <div
          key={key}
          className="flex items-baseline justify-between gap-[12px] border-t border-line-soft py-[7px]"
        >
          <dt className="text-[14px]/[19.6px] text-ink-2">{key}</dt>
          <dd className="font-mono text-[14px]/[19.6px] font-semibold text-ink">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A headline figure.
 *
 * **Sans-serif, 600 — not mono**, which is the frames' rule and the opposite of what this file did
 * first. Measured: the streak is 46px, Pace's deficit 52px, the backlog and today's rate 32px,
 * predicted recall 26px, the required rate 28px. Mono is for *columns* of numbers, where the point
 * is that digits line up; a single figure in mono just looks like a terminal.
 *
 * The `unit` sits **beside** the number at body size, because that is how the frames read every one
 * of them — `1,379 days`, `71 due and unreviewed`, `23 days until the deck runs dry`. The panel
 * heading is the label, so `note` underneath is for a second fact rather than for naming the first.
 */
export function StatValue({
  value,
  unit,
  note,
  size = 32,
  tone = "var(--ink)",
}: {
  value: string;
  unit?: string;
  note?: string;
  size?: number;
  tone?: string;
}) {
  return (
    <div className="flex flex-col gap-[4px]">
      <span className="flex flex-wrap items-baseline gap-[7px]">
        <span className="font-semibold" style={{ color: tone, fontSize: size, lineHeight: 1 }}>
          {value}
        </span>
        {unit && <span className="text-[15px]/[1.3] text-ink-2">{unit}</span>}
      </span>
      {note && <span className="text-[14px]/[1.5] text-ink-2">{note}</span>}
    </div>
  );
}
